import { useEffect, useRef, useCallback } from 'react';

interface SwipeCallbacks {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onDragStart?: () => void;
  onDragMove?: (deltaX: number, progress: number) => void;
  onDragEnd?: () => void;
}

interface TouchData {
  startX: number;
  startY: number;
  startTime: number;
  currentX: number;
  currentY: number;
}

const SWIPE_THRESHOLD = 50; // Minimum distance in pixels
const VELOCITY_THRESHOLD = 0.3; // Minimum velocity in pixels/ms
const MAX_VERTICAL_RATIO = 1.5; // Max ratio of vertical to horizontal movement

export const useSwipeNavigation = (callbacks: SwipeCallbacks, enabled: boolean = true) => {
  const touchDataRef = useRef<TouchData | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const isScrollableElement = (element: Element): boolean => {
    const style = window.getComputedStyle(element);
    const overflowY = style.overflowY;
    return overflowY === 'auto' || overflowY === 'scroll';
  };

  const isInteractiveElement = (element: Element): boolean => {
    const tagName = element.tagName.toLowerCase();
    const interactiveTags = ['input', 'textarea', 'select', 'button', 'a'];
    
    if (interactiveTags.includes(tagName)) return true;
    
    // Check for carousel or swipeable components
    const hasSwipeClass = element.className && 
      (element.className.includes('swiper') || 
       element.className.includes('carousel') ||
       element.className.includes('embla'));
    
    return hasSwipeClass;
  };

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!enabled) return;

    const touch = e.touches[0];
    const target = e.target as Element;

    // Check if target or any parent is scrollable or interactive
    let currentElement: Element | null = target;
    while (currentElement && currentElement !== containerRef.current) {
      if (isScrollableElement(currentElement) || isInteractiveElement(currentElement)) {
        return;
      }
      currentElement = currentElement.parentElement;
    }

    touchDataRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now(),
      currentX: touch.clientX,
      currentY: touch.clientY,
    };

    if (callbacks.onDragStart) {
      callbacks.onDragStart();
    }
  }, [enabled, callbacks]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!enabled || !touchDataRef.current) return;

    const touch = e.touches[0];
    touchDataRef.current.currentX = touch.clientX;
    touchDataRef.current.currentY = touch.clientY;

    if (callbacks.onDragMove) {
      const deltaX = touch.clientX - touchDataRef.current.startX;
      const progress = Math.abs(deltaX) / window.innerWidth;
      callbacks.onDragMove(deltaX, progress);
    }
  }, [enabled, callbacks]);

  const handleTouchEnd = useCallback(() => {
    if (!enabled || !touchDataRef.current) return;

    const touchData = touchDataRef.current;
    const deltaX = touchData.currentX - touchData.startX;
    const deltaY = touchData.currentY - touchData.startY;
    const deltaTime = Date.now() - touchData.startTime;

    const distanceX = Math.abs(deltaX);
    const distanceY = Math.abs(deltaY);
    const velocity = distanceX / deltaTime;

    // Calculate angle deviation from horizontal
    const angle = Math.abs(Math.atan2(deltaY, deltaX) * (180 / Math.PI));
    const isHorizontal = angle < 30 || angle > 150;

    // Check if this is a valid horizontal swipe
    if (
      distanceX > SWIPE_THRESHOLD &&
      velocity > VELOCITY_THRESHOLD &&
      isHorizontal &&
      distanceY < distanceX * MAX_VERTICAL_RATIO
    ) {
      if (deltaX > 0 && callbacks.onSwipeRight) {
        callbacks.onSwipeRight();
      } else if (deltaX < 0 && callbacks.onSwipeLeft) {
        callbacks.onSwipeLeft();
      }
    }

    if (callbacks.onDragEnd) {
      callbacks.onDragEnd();
    }

    touchDataRef.current = null;
  }, [enabled, callbacks]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd]);

  return containerRef;
};
